import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import toast from "react-hot-toast";

import { DataTable } from "../../components/data-table";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import { apiQueryKey, useApiPostMutation } from "../../lib/api";
import { usePointsClaimRequestsQuery } from "../../lib/api/usePointsClaimRequestsQuery";
import RewardModal from "../orders/RewardModal";
import {
  buildPointsClaimTableRows,
  createPointsClaimTableColumns,
  type PointsClaimTableRow,
} from "./pointsClaimTable";
import TaskApplicationsPanel from "./TaskApplicationsPanel";

const LIMIT_CHOICES = [10, 25, 50] as const;

function errMessage(err: unknown): string {
  if (
    err &&
    typeof err === "object" &&
    "response" in err &&
    err.response &&
    typeof err.response === "object" &&
    "data" in err.response &&
    err.response.data &&
    typeof err.response.data === "object" &&
    "message" in err.response.data
  ) {
    const m = (err.response.data as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "Something went wrong";
}

const RewardsOverview: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const applicationsTab =
    searchParams.get("tab") === "applications" ? true : false;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<typeof LIMIT_CHOICES[number]>(
    LIMIT_CHOICES[0]
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<PointsClaimTableRow | null>(null);

  useEffect(() => {
    setPage(1);
  }, [limit]);

  const { data, isPending, isError, error, refetch, isFetching } =
    usePointsClaimRequestsQuery({
      page,
      limit,
      enabled: !applicationsTab,
    });

  const meta = data?.meta;

  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: apiQueryKey("points-claim-requests"),
    });
  }, [queryClient]);

  const actionMutation = useApiPostMutation<
    unknown,
    { id: string; action: "approve" | "decline" }
  >((v) => `api/tasks/points-claim-requests/${v.id}`, {
    body: (v) => ({ action: v.action }),
    onSuccess: (_res, variables) => {
      toast.success(
        `Reward ${variables.action === "approve" ? "approved" : "declined"}`
      );
      invalidateList();
    },
    onError: (e) => {
      toast.error(errMessage(e), { duration: 4000 });
    },
  });

  const actingId =
    actionMutation.isPending && actionMutation.variables
      ? actionMutation.variables.id
      : null;

  const tableRows = useMemo(
    () => buildPointsClaimTableRows(data?.claimRequests ?? []),
    [data?.claimRequests]
  );

  const openDetail = useCallback((row: PointsClaimTableRow) => {
    setSelected(row);
    setDetailOpen(true);
  }, []);

  const columns = useMemo(
    () =>
      createPointsClaimTableColumns({
        actingId,
        onView: openDetail,
        onApprove: (row) =>
          actionMutation.mutate({ id: row.raw._id, action: "approve" }),
        onDecline: (row) =>
          actionMutation.mutate({ id: row.raw._id, action: "decline" }),
      }),
    [actingId, actionMutation, openDetail]
  );

  const totalPages = Math.max(1, meta?.totalPages ?? 1);
  const totalRecords = meta?.totalRecords ?? 0;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <div className="flex h-[31px] w-full shrink-0 items-center justify-center bg-[#1B1B1B]">
        {IS_STAGING ? (
          <h4 className="text-[12px] font-medium text-[#AEBCC6]">
            You are in Staging:{" "}
            <a href={PROD_LINK} className="text-[#03C668]">
              Switch to Prod
            </a>
          </h4>
        ) : (
          <h4 className="text-[12px] font-medium text-[#AEBCC6]">
            You are in PROD:{" "}
            <a href={STAGING_LINK} className="text-[#DF4E4E]">
              Switch to Staging
            </a>
          </h4>
        )}
      </div>
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lgss:flex-row">
        <Sidebar />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-[#13181B] lgss:w-10/12">
          <Navbar />
          <div className="mx-auto w-full max-w-[1600px] flex-1 px-5 pb-24 pt-6 font-manrope mds:px-8 lgss:px-10">
            <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
                  Rewards
                </h1>
                <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[#869AA9]">
                  {applicationsTab
                    ? "Review user applications for marketing tasks and approve completions. Create new tasks for the app when needed."
                    : "Review points-to-cash claim requests and task applications. Approve or decline pending payouts; open a row for bank details."}
                </p>
                <div
                  className="mt-5 inline-flex rounded-xl border border-[#2C363E] bg-[#161E25] p-1"
                  role="tablist"
                  aria-label="Rewards sections"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!applicationsTab}
                    onClick={() => setSearchParams({})}
                    className={`rounded-lg px-4 py-2 text-[13px] font-semibold transition ${
                      !applicationsTab
                        ? "bg-secondary_button text-white shadow-sm"
                        : "text-[#869AA9] hover:text-white"
                    }`}
                  >
                    Points claims
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={applicationsTab}
                    onClick={() => setSearchParams({ tab: "applications" })}
                    className={`rounded-lg px-4 py-2 text-[13px] font-semibold transition ${
                      applicationsTab
                        ? "bg-secondary_button text-white shadow-sm"
                        : "text-[#869AA9] hover:text-white"
                    }`}
                  >
                    Task applications
                  </button>
                </div>
              </div>
              {!applicationsTab ? (
                <div className="flex w-full flex-col gap-3 rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] sm:flex-row sm:items-end lg:w-auto">
                  <div className="min-w-[8rem] flex-1 sm:flex-initial sm:w-40">
                    <label
                      htmlFor="rewards-limit"
                      className="mb-1 block text-[11px] text-[#5C6B76]"
                    >
                      Rows per page
                    </label>
                    <select
                      id="rewards-limit"
                      value={limit}
                      onChange={(e) =>
                        setLimit(
                          Number(e.target.value) as typeof LIMIT_CHOICES[number]
                        )
                      }
                      className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                    >
                      {LIMIT_CHOICES.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="h-10 shrink-0 rounded-lg border border-[#2C363E] bg-[#1C2227] px-4 text-[13px] font-medium text-[#E7EBEE] transition hover:bg-[#273037]"
                  >
                    Refresh
                  </button>
                </div>
              ) : null}
            </header>

            {applicationsTab ? (
              <TaskApplicationsPanel />
            ) : isError ? (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-6 text-sm text-red-200">
                {errMessage(error)}{" "}
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="ml-2 underline"
                >
                  Retry
                </button>
              </div>
            ) : isPending && !data ? (
              <div className="flex justify-center py-20">
                <TailSpin color="#0B75C2" height={56} width={56} />
              </div>
            ) : (
              <div className="relative">
                {isFetching ? (
                  <div className="pointer-events-none absolute right-2 top-2 z-10 opacity-80">
                    <TailSpin color="#60B7F6" height={28} width={28} />
                  </div>
                ) : null}
                <DataTable
                  columns={columns}
                  data={tableRows}
                  currentPage={page}
                  setCurrentPage={setPage}
                  totalPage={totalPages}
                  limit={limit}
                  count={totalRecords}
                  mobileLayout="cards"
                  fixedBodyHeight="min(560px, 62vh)"
                  emptyContent={
                    <p className="py-6 text-adminTable-muted">
                      No claim requests on this page.
                    </p>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {detailOpen && selected ? (
        <RewardModal setRewardModal={setDetailOpen} reward={selected.raw} />
      ) : null}
    </div>
  );
};

export default RewardsOverview;
